import {
  DeleteInspectionMutation,
  GetInspectionConfigurationQuery,
  GetInspectionConfigurationQueryVariables,
  GetInspectionsQuery,
  GetInspectionsQueryVariables,
  InspectionListFragment,
} from '@/api/ariadne/__generated_types__/graphql';
import { ADD_INSPECTION } from '@/api/ariadne/mutations/add-inspection';
import { DELETE_INSPECTION } from '@/api/ariadne/mutations/delete-inspection';
import { GET_FILTERED_INSPECTIONS } from '@/api/ariadne/queries/get-filtered-inspections-query';
import { GET_INSPECTION_CONFIGURATION } from '@/api/ariadne/queries/get-inspection-configuration-query';
import * as dataSelectors from '@/data/data-selectors';
import { StreetAddress } from '@/license/components/license-content-styles';
import { PropertyLicenseStatus } from '@/property-details/components/property-license-status';
import { usePropertyDetails } from '@/utils/hooks/data/usePropertyDetails';
import useRegion from '@/utils/hooks/useRegion';
import { H6 } from '@/widgets/components/title-styles';
import { VerifiedIcon } from '@/widgets/components/verified-icon';
import { useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'rsuite';
import styled from 'styled-components';
import {
  BookInspectionFormValues,
  BookInspectionsModal,
} from './book-inspection-modal';
import { InspectionDetails } from './inspection-details';
import { InspectionsSkeleton } from './inspection-skeleton';
import { PropertyInspections } from './property-inspections';

const PropertyInspectionsWrapper = styled.div`
  padding: 0 24px;
  height: 40vh;
`;

const PropertyInspectionHeader = styled.div`
  display: grid;
  grid-template-columns: 5fr 1fr;
  gap: 16px;
`;

const ButtonWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: end;
`;

const VerifiedPropertyIcon = styled(VerifiedIcon)`
  font-size: 25px;
  flex-shrink: 0;
`;

export function InspectionsContent() {
  const { region: regionId } = useRegion();

  const { currentProperty: property, loading: propertyLoading } =
    usePropertyDetails();

  const [bookInspectionModalOpen, setBookInspectionModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] =
    useState<InspectionListFragment | null>(null);

  const licenses = useSelector(dataSelectors.licensesArray);

  const onBookInspection = () => {
    setBookInspectionModalOpen(true);
  };

  const onBookInspectionClose = () => {
    setBookInspectionModalOpen(false);
  };

  const onBookInspectionSubmit = (
    formValue: BookInspectionFormValues
  ): void => {
    const inspectionValue = {
      ...formValue,
      apn: property.apn,
    };
    addInspection({
      variables: { regionId, inspection: inspectionValue },
      onCompleted: () => {
        onBookInspectionClose();
        refetch();
      },
    });
  };

  const onCloseDetails = () => {
    setSelectedInspection(null);
  };

  const { data: inspectionConfig } = useQuery<
    GetInspectionConfigurationQuery,
    GetInspectionConfigurationQueryVariables
  >(GET_INSPECTION_CONFIGURATION, {
    variables: { regionId },
  });

  const {
    data: inspectionsResult,
    loading,
    refetch,
  } = useQuery<GetInspectionsQuery, GetInspectionsQueryVariables>(
    GET_FILTERED_INSPECTIONS,
    {
      variables: { regionId },
      notifyOnNetworkStatusChange: true,
    }
  );

  const [addInspection, { loading: addLoading }] = useMutation(ADD_INSPECTION);
  const [deleteInspection, { loading: deleteLoading }] =
    useMutation(DELETE_INSPECTION);

  const handleSelectInspection = (inspection: InspectionListFragment) => {
    setSelectedInspection(inspection);
  };

  const handleCancelInspection = (inspection: InspectionListFragment) => {
    deleteInspection({
      variables: { regionId, inspectionId: inspection.id as string },
      onCompleted: (data: DeleteInspectionMutation) => {
        if (data.deleteInspection) {
          refetch();
        }
      },
    });
  };

  const inspections: InspectionListFragment[] = useMemo(() => {
    if (!property) {
      return [];
    }

    if (inspectionsResult?.getFilteredInspections?.allInspections)
      return (
        inspectionsResult?.getFilteredInspections
          ?.allInspections as InspectionListFragment[]
      ).filter((inspection) => {
        // Temporarily filter the inspections by apn
        // This will be implemented in the API
        // Currently we're getting a maximum of 100 Inspections so the
        // information could not be accurate
        return inspection.apn === property.apn;
      });
    return [];
  }, [inspectionsResult, property]);

  const propertyTypes = useMemo(() => {
    if (!inspectionConfig?.getCreateInspectionConfig?.propertyTypes) {
      return [];
    }
    return inspectionConfig?.getCreateInspectionConfig?.propertyTypes.map(
      (type) => {
        return {
          label: type?.name,
          value: type?.type,
        };
      }
    );
  }, [inspectionConfig]);

  const inspectionTypes = useMemo(() => {
    if (!inspectionConfig?.getCreateInspectionConfig?.inspectionTypes) {
      return [];
    }
    return inspectionConfig?.getCreateInspectionConfig?.inspectionTypes.map(
      (type) => {
        return {
          label: type?.name,
          value: type?.type,
        };
      }
    );
  }, [inspectionConfig]);

  const roles = useMemo(() => {
    if (!inspectionConfig?.getCreateInspectionConfig?.inspectionRole) {
      return [];
    }
    return inspectionConfig?.getCreateInspectionConfig?.inspectionRole.map(
      (type) => {
        return {
          label: type?.name,
          value: type?.type,
        };
      }
    );
  }, [inspectionConfig]);

  if (propertyLoading || loading) {
    return <InspectionsSkeleton />;
  }

  return (
    <PropertyInspectionsWrapper>
      <PropertyInspectionHeader>
        <div>
          <StreetAddress>
            {property?.address}
            <VerifiedPropertyIcon verifiedStatus={property.verifiedStatus} />
          </StreetAddress>
          <PropertyLicenseStatus
            shortTermRental={property.shortTermRental}
            strLicense={property.strLicense}
            licenses={licenses}
            licenseId={property.licenseId}
          />
          <H6>Inspection History</H6>
        </div>
        <ButtonWrapper>
          <Button appearance="primary" onClick={onBookInspection}>
            Book Inspection
          </Button>
          <BookInspectionsModal
            open={bookInspectionModalOpen}
            property={property}
            onClose={onBookInspectionClose}
            onSubmitInspection={onBookInspectionSubmit}
            propertyTypes={propertyTypes}
            inspectionTypes={inspectionTypes}
            roles={roles}
            isLoading={addLoading}
          />
        </ButtonWrapper>
      </PropertyInspectionHeader>
      <PropertyInspections
        inspections={inspections}
        loading={loading || addLoading || deleteLoading}
        onSelectInspection={handleSelectInspection}
        onCancelInspection={handleCancelInspection}
        propertyAddress={property.address}
      />
      {selectedInspection && selectedInspection.id && (
        <InspectionDetails
          inspectionId={selectedInspection.id}
          regionId={regionId}
          onClose={onCloseDetails}
          configuration={
            inspectionConfig?.getCreateInspectionConfig
              ?.onSiteInspectionSections || []
          }
        />
      )}
    </PropertyInspectionsWrapper>
  );
}
